/**
 * Costos: gasto en proveedores por día y proveedor, por tipo de trabajo, las
 * cuentas que más gastan y el costo por hora de audio.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { useApp } from '../../components/AppContext'
import { BarrasApiladas, type SerieApilada } from '../../components/charts'
import { ErrorBox, Skeleton } from '../../components/ui'
import { clp, compacto, diaCorto, usd, usdEje } from '../../lib/adminFormat'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { Kpi, Periodo, Seccion } from './common'

/** El color sigue al proveedor (nunca a su posición en la lista). */
const PROVEEDORES: SerieApilada[] = [
  { clave: 'assemblyai', nombre: 'AssemblyAI', color: 1 },
  { clave: 'anthropic', nombre: 'Anthropic', color: 2 },
  { clave: 'deepgram', nombre: 'Deepgram', color: 3 },
  { clave: 'otros', nombre: 'Otros', color: 4 }
]

const TRABAJO_LABEL: Record<string, string> = {
  'transcribe-submit': 'Transcripción',
  summarize: 'Notas',
  ask: 'Preguntas a una reunión',
  'ask-across': 'Preguntas a varias reuniones'
}

export function Costos() {
  useDocumentTitle('Costos · Administración')
  const { api } = useApp()
  const [dias, setDias] = useState(30)
  const c = useAsync(() => api.adminCostos(dias), [api, dias])

  if (c.error) return <ErrorBox error={c.error} onRetry={() => void c.reload()} />
  if (!c.data) return <Skeleton h={260} />
  const d = c.data
  const tc = d.tipoCambioClpUsd
  const conocidos = new Set(PROVEEDORES.map((p) => p.clave))
  const valores: Record<string, Record<string, number>> = {}
  for (const x of d.porDia) {
    const clave = conocidos.has(x.proveedor) ? x.proveedor : 'otros'
    const dia = (valores[x.dia] ??= {})
    dia[clave] = (dia[clave] ?? 0) + x.usd
  }
  const dias_ = d.serieDias
  const presentes = PROVEEDORES.filter((p) => dias_.some((dd) => (valores[dd]?.[p.clave] ?? 0) > 0))
  const porHora = d.horasAudio ? d.totalUsd / d.horasAudio : 0
  const hoy = dias_[dias_.length - 1]

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="admin-toolbar">
        <span className="grow" />
        <Periodo dias={dias} onChange={setDias} />
      </div>
      <div className="kpis">
        <Kpi label="Costo del período" valor={usd(d.totalUsd)} nota={`≈ ${clp(d.totalUsd * tc)}`} />
        <Kpi label="Horas de audio" valor={`${compacto(d.horasAudio)} h`} />
        <Kpi
          label="Costo por hora"
          valor={porHora ? usd(porHora) : '—'}
          nota={`≈ ${clp(porHora * tc)}`}
        />
        <Kpi
          label="Gasto de hoy (UTC)"
          valor={usd(d.gastoHoyUsd)}
          nota={`de US$ ${d.presupuestoDiarioUsd} de tope; se reinicia a las 00:00 UTC`}
        />
      </div>
      {presentes.length > 0 ? (
        <BarrasApiladas
          titulo="Costo por día y proveedor"
          subtitulo={hoy ? `Hasta el ${diaCorto(hoy)}, en dólares` : undefined}
          dias={dias_}
          series={presentes}
          valores={valores}
          formato={usd}
          formatoEje={usdEje}
        />
      ) : (
        <div className="empty">
          <h2>Sin gasto en este período</h2>
        </div>
      )}
      <div className="admin-grid">
        <div className="span-6">
          <Seccion titulo="Por tipo de trabajo">
            <table className="simple">
              <caption className="sr">Costo por tipo de trabajo</caption>
              <tbody>
                {d.porTrabajo.map((t) => (
                  <tr key={t.job}>
                    <td>{TRABAJO_LABEL[t.job] ?? t.job}</td>
                    <td className="num">{usd(t.usd)}</td>
                    <td className="num faint">
                      {d.totalUsd ? `${Math.round((t.usd / d.totalUsd) * 100)} %` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Seccion>
        </div>
        <div className="span-6">
          <Seccion titulo="Cuentas que más gastan">
            <table className="simple">
              <caption className="sr">Cuentas que más gastan</caption>
              <thead>
                <tr>
                  <th scope="col">Cuenta</th>
                  <th scope="col" className="num">
                    Costo
                  </th>
                  <th scope="col" className="num">
                    Por hora
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.cuentas.map((x) => (
                  <tr key={x.orgId}>
                    <td>
                      {x.email ? (
                        <Link
                          className="link"
                          to={`/admin/usuarios?q=${encodeURIComponent(x.email)}`}
                        >
                          {x.email}
                        </Link>
                      ) : (
                        <span className="mono faint">{x.orgId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="num">{usd(x.usd)}</td>
                    <td className="num faint">{x.minutos ? usd(x.usd / (x.minutos / 60)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Seccion>
        </div>
      </div>
    </div>
  )
}
